/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { uuid } from '@src/app/model/abstract-object';
import { Txn } from '@src/app/model/txn';

/**
 * One row of the purchase documents listing for a cycle - a transaction and, if it has one, a
 * document attached to it. A transaction with several documents produces several rows; one with
 * none produces a single row with a null attachmentId, which is how the contents file knows to
 * report it as missing.
 *
 * Written by PurchaseController::getCycleDocuments.
 */
export class PurchaseDocument {
    txnId: uuid;
    typeId: number;
    /** An integer column - txns.refNr is a per team, per type sequence number, not a code. */
    refNr: number;
    txnDate: string;
    reference: string;
    debit: number;
    credit: number;
    notes: string;
    supplierName: string;

    attachmentId: uuid;
    /** What the user called the file when they attached it - free text, never used on disk. */
    attachmentName: string;
    attachmentNotes: string;
    ext: string;
    size: number;

    /** Folder the file is written into, relative to the folder the user picked. Set by decorate(). */
    folder: string;
    /** Name the file is written under - refNr-00.pdf. Set by decorate(). */
    saveAs: string;

    get hasDocument(): boolean {
        return !!this.attachmentId;
    }

    /** Where the file ends up, as written into the contents file. */
    get path(): string {
        return this.hasDocument ? `${this.folder}/${this.saveAs}` : '';
    }

    get typeName(): string {
        return Txn.getType(this.typeId)?.name ?? String(this.typeId);
    }

    get amount(): number {
        return (this.debit ?? 0) - (this.credit ?? 0);
    }
}

/** Top level folder per transaction type - the three kinds of purchase an accountant asks for. */
export const PURCHASE_DOC_FOLDERS: Record<number, string> = {
    [Txn.TYPE.PURCHASE.id]: 'purchases',
    [Txn.TYPE.PURCHASE_CN.id]: 'purchaseCredits',
    [Txn.TYPE.CASH_PURCHASE.id]: 'cashPurchases',
};

/** Anything posted to the purchase ledger without a supplier still needs somewhere to go. */
export const NO_SUPPLIER_FOLDER = '_noSupplier';

/** Long supplier names would push Windows past its 260 character path limit on their own. */
const MAX_FOLDER_CHARS = 60;

/**
 * The folder everything is written into, created inside the one the user picked.
 *
 * Named for the team and the cycle because a managing agent runs this for every OMC they look
 * after, one after another, into the same downloads folder - without the team in the name, last
 * week's OMC and this week's would land on top of each other.
 *
 * The teamId goes in as it stands: it is in every URL for the tenant, so it is already limited to
 * characters a file system accepts. Only the cycle name is free text.
 */
export function purchaseDocsFolderName(teamId: uuid, cycleName: string): string {
    return `${teamId}-${fileSystemSafe(cycleName)}-purchaseDocs`;
}

/**
 * Turn the rows the server sent into documents that know where they are going.
 *
 * Numbering restarts per transaction, so the first document on a transaction is always -00. The
 * server orders the rows, so running the same cycle twice writes the same names - which is what
 * makes a re-run after a correction overwrite cleanly instead of accumulating duplicates.
 */
export function decoratePurchaseDocuments(rows: PurchaseDocument[]): PurchaseDocument[] {
    const counts = new Map<uuid, number>();

    for (const row of rows) {
        row.folder = documentFolder(row);
        if (!row.hasDocument) {
            continue;
        }
        const index = counts.get(row.txnId) ?? 0;
        counts.set(row.txnId, index + 1);
        row.saveAs = documentName(row, index);
    }

    return rows;
}

/**
 * Type first, then supplier - an accountant asking "show me everything from this supplier" is
 * looking within one kind of document, and a credit note filed beside the invoices it credits
 * would be read as another invoice.
 */
function documentFolder(row: PurchaseDocument): string {
    const top = PURCHASE_DOC_FOLDERS[row.typeId] ?? 'other';

    return `${top}/${supplierFolder(row.supplierName)}`;
}

/**
 * Supplier names are free text, so they get flattened to something every file system accepts.
 * Two suppliers can flatten to the same folder - "Smith & Co" and "Smith and Co" would - and that
 * is accepted rather than worked around: the contents file names the supplier on every line, so
 * the pair can still be told apart there.
 */
export function supplierFolder(supplierName: string): string {
    const folder = fileSystemSafe(supplierName).substring(0, MAX_FOLDER_CHARS);

    return folder === '' ? NO_SUPPLIER_FOLDER : folder;
}

function documentName(row: PurchaseDocument, index: number): string {
    const ref = fileSystemSafe(row.refNr) || String(row.txnId);
    const ext = fileSystemSafe(row.ext);
    const suffix = String(index).padStart(2, '0');

    return ext === '' ? `${ref}-${suffix}` : `${ref}-${suffix}.${ext}`;
}

/**
 * Anything outside letters, digits, dot, dash and underscore becomes a dash. Trailing dots and
 * spaces are stripped as well as replaced - Windows silently rejects a name ending in either.
 *
 * Takes unknown rather than string because these values come straight off a JSON response: refNr
 * is a number, and any of them can be null.
 */
function fileSystemSafe(value: unknown): string {
    if (value === null || value === undefined) {
        return '';
    }

    return String(value)
        .replace(/[^A-Za-z0-9._-]+/g, '-').replace(/-+/g, '-').replace(/^[-.]+|[-.]+$/g, '');
}
